import React, { useEffect, useState, useMemo } from "react";
import { api, fmtGNF } from "@/lib/api";
import { BookOpen, ShoppingCart, Plus, Minus, X, Send, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const LEVELS = ["Tous", "Maternelle", "Primaire", "Collège", "Lycée"];

export default function BooksPage() {
  const [books, setBooks] = useState([]);
  const [cart, setCart] = useState({}); // {book_id: quantity}
  const [showCart, setShowCart] = useState(false);
  const [filter, setFilter] = useState("Tous");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/books").then(({ data }) => setBooks(data));
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    if (filter === "Tous") return books;
    return books.filter(b => (b.level || "").toLowerCase().includes(filter.toLowerCase()));
  }, [books, filter]);

  const cartLines = useMemo(() => Object.entries(cart).map(([id, q]) => {
    const b = books.find(x => x.id === id);
    return b ? { ...b, quantity: q, subtotal: b.price * q } : null;
  }).filter(Boolean), [cart, books]);
  const cartTotal = cartLines.reduce((s, l) => s + l.subtotal, 0);
  const cartCount = cartLines.reduce((s, l) => s + l.quantity, 0);

  const add = (b) => {
    if ((cart[b.id] || 0) + 1 > b.stock) { toast.error(`Stock épuisé pour ${b.title}`); return; }
    setCart(c => ({ ...c, [b.id]: (c[b.id] || 0) + 1 }));
    toast.success(`${b.title} ajouté au panier`);
  };
  const dec = (id) => setCart(c => {
    const next = { ...c };
    if (next[id] > 1) next[id]--; else delete next[id];
    return next;
  });
  const inc = (id) => {
    const b = books.find(x => x.id === id);
    if (!b) return;
    if ((cart[id] || 0) + 1 > b.stock) { toast.error("Stock max atteint"); return; }
    setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  };
  const remove = (id) => setCart(c => { const n = { ...c }; delete n[id]; return n; });

  const checkout = async () => {
    if (cartLines.length === 0) { toast.error("Panier vide"); return; }
    setSubmitting(true);
    try {
      const items = Object.entries(cart).map(([book_id, quantity]) => ({ book_id, quantity }));
      const { data } = await api.post("/orders", { items });
      toast.success(`Commande ${data.order_number} créée — Total ${fmtGNF(data.total)}`);
      setCart({});
      setShowCart(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la commande");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5" data-testid="page-books">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="chip mb-2"><BookOpen className="h-3 w-3" /><span>Boutique scolaire</span></div>
          <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Manuels & fournitures</h2>
          <p className="text-sm text-slate-500 mt-1">Commandez en ligne — retrait à l&apos;école sous 48h après confirmation.</p>
        </div>
        <Button onClick={() => setShowCart(true)} className="bg-[#EA580C] hover:bg-orange-700 text-white relative gap-2" data-testid="open-cart-btn">
          <ShoppingCart className="h-4 w-4" /> Mon panier
          {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#FBBF24] text-[#0F172A] text-xs font-bold flex items-center justify-center" data-testid="cart-count">{cartCount}</span>
          )}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {LEVELS.map(l => (
          <button
            key={l}
            onClick={() => setFilter(l)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === l ? "bg-[#1E3A8A] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
            data-testid={`filter-${l.toLowerCase()}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {visible.map((b) => {
          const inCart = cart[b.id] || 0;
          const lowStock = b.stock < 5;
          return (
            <div key={b.id} className="mazid-card p-5 flex flex-col" data-testid={`book-card-${b.id}`}>
              <div className="aspect-[3/4] rounded-lg bg-gradient-to-br from-[#1E3A8A] to-[#166534] flex items-center justify-center mb-4 text-white relative overflow-hidden">
                <BookOpen className="h-16 w-16 opacity-40" strokeWidth={1.4} />
                {lowStock && b.stock > 0 && <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider">Stock faible</div>}
                {b.stock === 0 && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold text-sm">Épuisé</div>}
              </div>
              <div className="chip mb-2 self-start">{b.level}</div>
              <h3 className="font-heading font-bold text-base text-[#0F172A] mb-2 leading-snug flex-1">{b.title}</h3>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <div className="text-xs text-slate-500">Stock: {b.stock}</div>
                  <div className="font-heading font-extrabold text-xl text-[#166534]">{fmtGNF(b.price)}</div>
                </div>
              </div>
              {inCart === 0 ? (
                <Button onClick={() => add(b)} disabled={b.stock === 0} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-1 text-xs w-full disabled:opacity-50" data-testid={`book-add-${b.id}`}>
                  <ShoppingCart className="h-3 w-3" /> Ajouter au panier
                </Button>
              ) : (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-1.5" data-testid={`book-qty-${b.id}`}>
                  <button onClick={() => dec(b.id)} className="w-6 h-6 rounded bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                  <div className="text-sm font-bold text-green-700">{inCart} au panier</div>
                  <button onClick={() => inc(b.id)} className="w-6 h-6 rounded bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                </div>
              )}
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-12 bg-white border border-slate-200 rounded-xl">Aucun manuel disponible pour ce niveau.</div>
        )}
      </div>

      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-2xl" data-testid="cart-dialog">
          <DialogHeader><DialogTitle>Mon panier ({cartCount} article{cartCount > 1 ? "s" : ""})</DialogTitle></DialogHeader>
          {cartLines.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-30" />
              Votre panier est vide.
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {cartLines.map(l => (
                  <div key={l.id} className="py-3 flex items-center gap-3" data-testid={`cart-line-${l.id}`}>
                    <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#1E3A8A] to-[#166534] flex items-center justify-center text-white flex-shrink-0">
                      <BookOpen className="h-5 w-5 opacity-60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#0F172A] truncate">{l.title}</div>
                      <div className="text-xs text-slate-500">{l.level} · {fmtGNF(l.price)} × {l.quantity}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => dec(l.id)} className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                      <span className="text-sm font-bold w-6 text-center">{l.quantity}</span>
                      <button onClick={() => inc(l.id)} className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                    </div>
                    <div className="text-sm font-bold text-[#166534] w-24 text-right">{fmtGNF(l.subtotal)}</div>
                    <button onClick={() => remove(l.id)} className="text-slate-400 hover:text-red-500 ml-2"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 pt-4 mt-2">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-slate-500">Total à régler à l&apos;école</span>
                  <span className="font-heading font-extrabold text-2xl text-[#1E3A8A]" data-testid="cart-total">{fmtGNF(cartTotal)}</span>
                </div>
                <Button onClick={checkout} disabled={submitting} className="w-full bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="checkout-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Valider la commande
                </Button>
                <p className="text-xs text-slate-400 text-center mt-2">Vous serez contacté(e) sous 48h pour le retrait.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
